<!--
  Venda parcelada (chamado 2026-0067) — o modal da venda inteira.

  Abre por eventBus ('modal.recebimento-parcelado.open', { id }) com QUALQUER parcela da venda; o
  RecebimentoModal redireciona para cá quando o recebimento aberto é parcela de uma venda manual,
  então toda tela que já abria "editar recebimento" chega aqui sem mudar nada.

  Salvar manda a venda inteira numa chamada (POST /recebimentos/salvar_parcelas, uma transação):
  os campos da venda e só o que mudou em cada parcela. Excluir apaga a venda (o delete de
  qualquer parcela apaga o grupo).
-->
<template>
  <div id="recebimento-parcelado-modal-app">
    <modal
      elem-id="modal_venda_parcelada"
      size="large"
      :close-label="t('Fechar')"
      :save-label="t('Salvar venda')"
      :save-disabled-fn="() => salvando || pendencias.length > 0 || !carregada"
      :save-fn="handleSave"
      :title="titulo"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal">

      <template v-if="isVisible">
        <div v-if="!carregada" class="carregando" data-testid="venda-parcelada-carregando">
          <i class="pi pi-spinner pi-spin"></i> {{ t('Carregando...') }}
        </div>

        <template v-else>
          <div class="action-buttons">
            <Button
              data-testid="venda-parcelada-excluir"
              :label="t('Excluir venda')"
              icon="pi pi-trash"
              severity="danger"
              size="small"
              @click="handleDelete" />
          </div>

          <recebimento-parcelado-form
            ref="formRef"
            :venda="venda"
            :parcelas="parcelas"
            :formas="formas">
          </recebimento-parcelado-form>

          <div v-if="pendencias.length > 0" class="pendencias-salvar" data-testid="venda-parcelada-pendencias">
            <i class="pi pi-exclamation-circle"></i>
            <div>
              <strong>{{ t('Para salvar, ainda falta') }}:</strong>
              <ul>
                <li v-for="motivo in pendencias" :key="motivo">{{ motivo }}</li>
              </ul>
            </div>
          </div>
        </template>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '@/eventBus'
import { log } from '@/utils/DebugLogger'
import { tComParametros } from '@/utils/tComParametros'
import { useConfirmAction } from '@/composables/useConfirmAction'
import { useFormasPagamento } from '@/composables/useFormasPagamento'
import { useRecebimento } from '@/composables/useRecebimento'
import { useVendaParcelada, type LinhaDaVenda, type VendaEditavel } from '@/composables/useVendaParcelada'
import { dataIso, mensagemDaRecusaDeParcelamento, mudancasDasParcelas, type ParcelaEditavel } from '@/composables/useParcelamento'
import type { FormasPagamento_FormaPagamentoRow } from '@/contracts/FormasPagamento_FormaPagamentoRow'
import RecebimentoParceladoForm from './RecebimentoParceladoForm.vue'

const { t } = useI18n()
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros)
const { confirmAction } = useConfirmAction()
const { listarFormasPagamento } = useFormasPagamento()
const { deleteRecebimento } = useRecebimento()
const { carregar, salvar } = useVendaParcelada()

const isVisible = ref(false)
const carregada = ref(false)
const salvando = ref(false)
const formRef = ref<{ pendencias: string[] } | null>(null)

const linhas = ref<LinhaDaVenda[]>([])
const formas = ref<FormasPagamento_FormaPagamentoRow[]>([])
const venda = ref<VendaEditavel>({ recebimento_forma_pagamento_id: null, recebimento_data: '', recebimento_observacoes: '' })
const vendaOriginal = ref<VendaEditavel>({ ...venda.value })
const parcelas = ref<ParcelaEditavel[]>([])
const parcelasOriginais = ref<ParcelaEditavel[]>([])

const pendencias = computed<string[]>(() => formRef.value?.pendencias ?? [])
const titulo = computed(() => carregada.value
  ? tp('Venda parcelada em {n}x', { n: parcelas.value.length })
  : t('Venda parcelada'))

const paraParcela = (l: LinhaDaVenda): ParcelaEditavel => ({
  recebimento_id: Number(l.recebimento_id),
  recebimento_parcela_numero: Number(l.recebimento_parcela_numero ?? 1),
  recebimento_valor: Number(l.recebimento_valor ?? 0),
  recebimento_valor_taxas: Number(l.recebimento_valor_taxas ?? 0),
  recebimento_data_disponivel: dataIso(l.recebimento_data_disponivel),
})

const open = async (payload?: { id?: number | string }): Promise<void> => {
  const id = Number(payload?.id ?? 0)
  if (!id) return
  log('RecebimentoParceladoModal', 'open', 'Abrindo venda parcelada', id)
  carregada.value = false
  isVisible.value = true
  try {
    const venda_ = await carregar(id)
    const primeira = venda_[0]
    linhas.value = venda_
    formas.value = await listarFormasPagamento(false, Number(primeira.recebimento_franquia_id)) as FormasPagamento_FormaPagamentoRow[]
    venda.value = {
      recebimento_forma_pagamento_id: primeira.recebimento_forma_pagamento_id ? Number(primeira.recebimento_forma_pagamento_id) : null,
      recebimento_data: dataIso(primeira.recebimento_data),
      recebimento_observacoes: String(primeira.recebimento_observacoes ?? ''),
    }
    vendaOriginal.value = { ...venda.value }
    parcelas.value = venda_.map(paraParcela)
    parcelasOriginais.value = venda_.map(paraParcela)
    carregada.value = true
  } catch (error) {
    log('RecebimentoParceladoModal', 'open', 'Erro ao carregar a venda', error)
    eventBus.emit('globalMessage.error', t('Erro ao carregar recebimento'))
    closeModal()
  }
}

/** Os mesmos avisos que o RecebimentoModal dá depois de salvar/excluir. */
const avisarMudanca = (): void => {
  const primeira = linhas.value[0]
  if (!primeira) return
  if (primeira.recebimento_origem_id) eventBus.emit(`refresh-recebimento-${primeira.recebimento_origem_id}`)
  eventBus.emit('recebimento.refresh')
  if (primeira.recebimento_origem === 'compromisso') eventBus.emit('compromisso.refresh')
  if (primeira.recebimento_origem === 'so_order') eventBus.emit('pdv.saved')
}

const handleSave = async (): Promise<void> => {
  if (pendencias.value.length > 0 || !linhas.value.length) return
  const v = venda.value
  const o = vendaOriginal.value
  salvando.value = true
  try {
    await salvar({
      recebimento_id: Number(linhas.value[0].recebimento_id),
      // Só o que mudou na venda; o backend trata ausente como "não muda".
      ...(v.recebimento_data !== o.recebimento_data ? { recebimento_data: v.recebimento_data } : {}),
      ...(v.recebimento_forma_pagamento_id !== o.recebimento_forma_pagamento_id && v.recebimento_forma_pagamento_id
        ? { recebimento_forma_pagamento_id: v.recebimento_forma_pagamento_id } : {}),
      ...(v.recebimento_observacoes !== o.recebimento_observacoes ? { recebimento_observacoes: v.recebimento_observacoes } : {}),
      parcelas: mudancasDasParcelas(parcelasOriginais.value, parcelas.value),
    })
    eventBus.emit('globalMessage.success', t('Venda parcelada salva.'))
    avisarMudanca()
    closeModal()
  } catch (error) {
    log('RecebimentoParceladoModal', 'handleSave', 'Erro ao salvar', error)
    eventBus.emit('globalMessage.error', mensagemDaRecusaDeParcelamento(error, t) ?? t('Erro ao salvar recebimento'))
  } finally {
    salvando.value = false
  }
}

const handleDelete = (): void => {
  confirmAction({
    message: tp('Excluir esta venda e as {n} parcelas dela?', { n: parcelas.value.length }),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await deleteRecebimento(Number(linhas.value[0].recebimento_id))
        avisarMudanca()
        closeModal()
      } catch (error) {
        log('RecebimentoParceladoModal', 'handleDelete', 'Erro ao excluir', error)
        // O servidor diz por que recusou (permissão, provedor de pagamento…); só na falta disso a genérica.
        eventBus.emit('globalMessage.error', (error instanceof Error && error.message) ? t(error.message) : t('Erro ao excluir recebimento'))
      }
    },
  })
}

const closeModal = (): void => {
  isVisible.value = false
  carregada.value = false
  linhas.value = []
  parcelas.value = []
  parcelasOriginais.value = []
}

onMounted(() => {
  eventBus.on('modal.recebimento-parcelado.open', open)
})

onBeforeUnmount(() => {
  eventBus.off('modal.recebimento-parcelado.open', open)
})
</script>

<style scoped>
.carregando {
  padding: 24px;
  text-align: center;
  color: #6c757d;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.pendencias-salvar {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 16px;
  background-color: #fff4e5;
  border: 1px solid #ffd8a8;
  border-radius: 6px;
  color: #8a5300;
  font-size: 0.9rem;
}

.pendencias-salvar ul {
  margin: 4px 0 0;
  padding-left: 18px;
}
</style>
